// src/hooks/useDescansos.ts
//
// Descansos puntuales del trabajador: lectura (por trabajador o por toda la
// empresa) para pintarlos en el calendario, y las dos acciones de alta/baja.
// Mismo patrón que useCalendarEvents.ts: fetch + canal realtime + reloadKey.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import type { RawDescanso, CalendarEvent } from '@/components/types/typeCalendar';
import { buildEventosDescansos } from '@/components/utils/calendarUtils';

/* ============================================================
   📌 Lectura — trabajador
============================================================ */
export function useDescansosPorTrabajador(
  idUsuario: string,
  idEmpresa: string,
  fechaInicio: string | null,
  fechaFin: string | null,
  reloadKey: number
): CalendarEvent[] {
  const [raw, setRaw] = useState<RawDescanso[]>([]);
  const fetchRef = useRef<() => void>(() => {});

  const fetchDescansos = useCallback(async () => {
    if (!idUsuario || idUsuario === 'skip' || !idEmpresa || !fechaInicio || !fechaFin) {
      setRaw([]);
      return;
    }
    const { data, error } = await supabase
      .from('descansos_trabajador')
      .select('id_descanso, id_usuario, id_empresa, fecha, hora_inicio, hora_fin, motivo')
      .eq('id_usuario', idUsuario)
      .eq('id_empresa', idEmpresa)
      .eq('activo', true)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (error) {
      Sentry.captureException(error, { extra: { idUsuario, idEmpresa, fechaInicio, fechaFin } });
      return;
    }
    setRaw((data ?? []) as RawDescanso[]);
  }, [idUsuario, idEmpresa, fechaInicio, fechaFin]);

  useEffect(() => {
    fetchRef.current = fetchDescansos;
  }, [fetchDescansos]);

  useEffect(() => {
    fetchDescansos();
  }, [fetchDescansos, reloadKey]);

  useEffect(() => {
    if (!idEmpresa || !idUsuario || idUsuario === 'skip') return;
    const channel = supabase
      .channel(`descansos-rt-trabajador-${idUsuario}-${idEmpresa}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'descansos_trabajador' },
        () => fetchRef.current?.()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [idUsuario, idEmpresa]);

  return useMemo(() => buildEventosDescansos(raw), [raw]);
}

/* ============================================================
   🏢 Lectura — empresa (todos los trabajadores)
============================================================ */
export function useDescansosPorEmpresa(
  idEmpresa: string,
  fechaInicio: string | null,
  fechaFin: string | null,
  reloadKey: number
): CalendarEvent[] {
  const [raw, setRaw] = useState<RawDescanso[]>([]);
  const fetchRef = useRef<() => void>(() => {});

  const fetchDescansos = useCallback(async () => {
    if (!idEmpresa || idEmpresa === 'skip' || !fechaInicio || !fechaFin) {
      setRaw([]);
      return;
    }
    const { data, error } = await supabase
      .from('descansos_trabajador')
      .select('id_descanso, id_usuario, id_empresa, fecha, hora_inicio, hora_fin, motivo')
      .eq('id_empresa', idEmpresa)
      .eq('activo', true)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa, fechaInicio, fechaFin } });
      return;
    }
    setRaw((data ?? []) as RawDescanso[]);
  }, [idEmpresa, fechaInicio, fechaFin]);

  useEffect(() => {
    fetchRef.current = fetchDescansos;
  }, [fetchDescansos]);

  useEffect(() => {
    fetchDescansos();
  }, [fetchDescansos, reloadKey]);

  useEffect(() => {
    if (!idEmpresa || idEmpresa === 'skip') return;
    const channel = supabase
      .channel(`descansos-rt-empresa-${idEmpresa}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'descansos_trabajador' },
        () => fetchRef.current?.()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [idEmpresa]);

  return useMemo(() => buildEventosDescansos(raw), [raw]);
}

/* ============================================================
   Alta / baja
============================================================ */
export interface CrearDescansoInput {
  id_usuario: string;
  id_empresa: string;
  fecha: string; // YYYY-MM-DD
  hora_inicio: string; // HH:MM
  hora_fin: string; // HH:MM
  motivo?: string;
}

/**
 * Crea el descanso. Se permite aunque se solape con una cita ya reservada;
 * en ese caso devuelve ok:true igualmente y el número de citas afectadas,
 * para que quien llama muestre el aviso (no bloqueante, según lo acordado).
 */
export async function crearDescanso(
  input: CrearDescansoInput
): Promise<{ ok: boolean; citasSolapadas?: number }> {
  const { data, error } = await supabase
    .rpc('crear_descanso_trabajador', {
      p_id_usuario: input.id_usuario,
      p_id_empresa: input.id_empresa,
      p_fecha: input.fecha,
      p_hora_inicio: input.hora_inicio,
      p_hora_fin: input.hora_fin,
      p_motivo: input.motivo?.trim() || null,
    })
    .maybeSingle();

  if (error) {
    Sentry.captureException(error, { extra: { input } });
    toast.error('No se pudo crear el descanso');
    return { ok: false };
  }

  return { ok: true, citasSolapadas: (data as any)?.citas_solapadas ?? 0 };
}

export async function eliminarDescanso(idDescanso: string): Promise<boolean> {
  const { error } = await supabase.rpc('eliminar_descanso_trabajador', {
    p_id_descanso: idDescanso,
  });
  if (error) {
    Sentry.captureException(error, { extra: { idDescanso } });
    toast.error('No se pudo borrar el descanso');
    return false;
  }
  return true;
}